import { CSVTable } from '@/app/chat/_common/csv-table'
import { MarkdownWithTypingEffect } from '@/app/chat/_common/markdown-with-typing-effect'
import { JSONStyled } from '@/components/ui/json-styled'
import { Skeleton } from '@/components/ui/skeleton'
import type { AnnotatedDataFrameContentPart, Message as MessageType } from '@/hooks/api/_types'
import { cn } from '@/lib/utils'
import React from 'react'
import { usePapaParse } from 'react-papaparse'
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter'

// Import only the languages you actually use
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css'
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json'
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx'
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown'
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup'
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python'
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql'
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript'
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml'
import { isURL as isUrl } from 'validator'

// Register only needed languages
SyntaxHighlighter.registerLanguage('jsx', jsx)
SyntaxHighlighter.registerLanguage('typescript', typescript)
SyntaxHighlighter.registerLanguage('json', json)
SyntaxHighlighter.registerLanguage('css', css)
SyntaxHighlighter.registerLanguage('markdown', markdown)
SyntaxHighlighter.registerLanguage('markup', markup)
SyntaxHighlighter.registerLanguage('python', python)
SyntaxHighlighter.registerLanguage('sql', sql)
SyntaxHighlighter.registerLanguage('yaml', yaml)

const Message = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    messageType: MessageType['messageContent']['role']
  }
>(({ messageType, children, className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex w-full flex-col break-words font-light',
      messageType === 'user' &&
        'min-h-16 w-fit justify-center self-end whitespace-pre-wrap rounded-lg border border-mango-100 bg-mango-50 p-4 lg:max-w-[75%]',
      messageType === 'assistant' && 'relative items-start gap-4 self-start pt-4',
      className,
    )}
    {...props}
  >
    {children}
  </div>
))
Message.displayName = 'Message'

const MessageLoading = () => (
  <Message className='gap-2' messageType='assistant'>
    <Skeleton className='h-4 w-72' />
    <Skeleton className='h-4 w-56' />
  </Message>
)

type MessageMarkdownProps = Readonly<{
  text: string
  renderIcons: (props: { text: string }) => React.ReactNode
  enableTypingAnimation?: boolean
}>

const MessageMarkdown = ({
  text: _text,
  renderIcons,
  enableTypingAnimation,
}: MessageMarkdownProps) => {
  const text = React.useMemo(
    () => _text.replaceAll(/!\[[^\]]+\]\(sandbox:\/[^\)]+\)/g, ''),
    [_text],
  )
  return (
    <Message messageType='assistant'>
      <MarkdownWithTypingEffect
        markdown={text}
        enableTypingAnimation={enableTypingAnimation}
        {...(enableTypingAnimation && {
          renderOnTypingComplete: () => renderIcons({ text }),
        })}
      />
      {!enableTypingAnimation && renderIcons({ text })}
    </Message>
  )
}

type MessageDataProps = Readonly<{
  data: any
  messageType?: MessageType['messageContent']['role']
  renderIcons?: (props: { dataString: string }) => React.ReactNode
}>

const MessageData = ({ data, messageType = 'assistant', renderIcons }: MessageDataProps) => {
  const dataString = JSON.stringify(data, null, 4)
  return (
    <Message messageType={messageType}>
      <JSONStyled data={data} />
      {renderIcons && renderIcons({ dataString })}
    </Message>
  )
}

type MessageImageProps = Readonly<{
  imgSrc: string
  renderIcons: (props: { imgSrc: string }) => React.ReactNode
}>

const MessageImage = ({ imgSrc, renderIcons }: MessageImageProps) => (
  <Message messageType='assistant'>
    <img className='w-full' src={imgSrc} alt='Generated response' />
    {renderIcons({ imgSrc })}
  </Message>
)

type MessageUserImageProps = Readonly<{
  text: string
}>

const MessageUserImage = ({ text }: MessageUserImageProps) => {
  const isValidUrl = isUrl(text)
  return (
    <Message messageType='user'>
      {isValidUrl ? <img className='w-full' src={text} alt='Submitted image' /> : text}
    </Message>
  )
}

type MessageCSVTableProps = Readonly<{
  part: AnnotatedDataFrameContentPart
  renderIcons: (props: { csvString: string }) => React.ReactNode
}>

function MessageCSVTable({ part, renderIcons }: MessageCSVTableProps) {
  const { jsonToCSV } = usePapaParse()
  const csvString = jsonToCSV(part.annotatedDataframe.headData)
  return (
    <Message messageType='assistant'>
      <CSVTable
        data={part.annotatedDataframe.headData}
        schema={part.annotatedDataframe.dataSchema}
      />
      {renderIcons({ csvString })}
    </Message>
  )
}

type MessageCodeProps = Readonly<{
  codeString: string
  language?: string
  renderIcons: (props: { codeString: string }) => React.ReactNode
  isCompact?: boolean
  className?: string
}>

function MessageCode({
  codeString,
  language,
  renderIcons,
  isCompact,
  className,
}: MessageCodeProps) {
  return (
    <Message
      messageType='assistant'
      className={cn(
        'overflow-x-auto overflow-y-visible pt-0 text-sm [&>pre]:w-[inherit] [&>pre]:rounded [&_.react-syntax-highlighter-line-number]:opacity-55',
        isCompact && '!p-0 text-xs [&_pre]:!p-0',
        className,
      )}
    >
      <SyntaxHighlighter
        wrapLongLines
        showLineNumbers
        lineNumberStyle={isCompact ? { minWidth: 0, whiteSpace: 'nowrap' } : {}}
        language={language}
      >
        {codeString}
      </SyntaxHighlighter>
      {renderIcons({ codeString })}
    </Message>
  )
}

export {
  Message,
  MessageCode,
  MessageCSVTable,
  MessageData,
  MessageImage,
  MessageLoading,
  MessageMarkdown,
  MessageUserImage,
}
